// src/components/modals/FunnelCustomBuilderModal.jsx
import React, { useMemo, useState } from "react";
import { Modal, Button, TextInput, Select, Badge } from "flowbite-react";

const VIS_MAP = {
  PRIVATE: { label: "Somente eu", badge: { color: "gray" } },
  TEAM: { label: "Time", badge: { color: "purple" } },
  PUBLIC: { label: "Público", badge: { color: "indigo" } },
};

export default function FunnelCustomBuilderModal({
  show,
  onClose,
  onCreate, // (payloadPronto) => Promise
  defaultVisibility = "PUBLIC",
  defaultTeamId = null,
  defaultTeamName = "",
  ownerUserId = null,
}) {
  const vis = String(defaultVisibility || "PUBLIC").toUpperCase();
  const visInfo = VIS_MAP[vis] || VIS_MAP.PUBLIC;

  const [name, setName] = useState("");
  const [stages, setStages] = useState(["Etapa 1", "Etapa 2"]);
  const [closingIndex, setClosingIndex] = useState(""); // string para Select
  const [error, setError] = useState("");

  const canSave = useMemo(
    () => name.trim() && stages.length > 0,
    [name, stages]
  );

  const addStage = () => setStages((s) => [...s, `Etapa ${s.length + 1}`]);
  const updateStage = (i, v) =>
    setStages((s) => s.map((x, idx) => (idx === i ? v : x)));
  const removeStage = (i) => {
    setStages((s) => s.filter((_, idx) => idx !== i));
    // se remover o fechamento selecionado, limpa
    if (closingIndex !== "" && Number(closingIndex) === i) setClosingIndex("");
  };
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= stages.length) return;
    const s = [...stages];
    const [m] = s.splice(i, 1);
    s.splice(j, 0, m);
    setStages(s);
    // ajusta closingIndex se precisar
    if (closingIndex !== "") {
      if (Number(closingIndex) === i) setClosingIndex(String(j));
      else if (Number(closingIndex) === j) setClosingIndex(String(i));
    }
  };

  const buildPayload = () => {
    const idx = closingIndex === "" ? -1 : Number(closingIndex);
    return {
      name: name.trim(),
      visibility: vis,
      teamId: vis === "TEAM" ? defaultTeamId : null,
      ownerUserId: vis === "PRIVATE" ? ownerUserId : null,
      closingStageId: null,
      stages: stages.map((nm, i) => ({
        name: nm.trim() || `Etapa ${i + 1}`,
        order: i + 1,
        probability: 0,
        color: null,
        isWin: i === idx,
        isLost: false,
        autoLeadStatus: null,
        requiredFields: [],
        autoTasks: [],
        slaHours: null,
        wipLimit: null,
        webhookOnEnter: null,
      })),
    };
  };

  const handleCreate = async () => {
    try {
      setError("");
      if (!canSave) {
        setError("Informe o nome do quadro e ao menos uma etapa.");
        return;
      }
      await onCreate?.(buildPayload());
      onClose?.();
    } catch (e) {
      setError("Não foi possível criar o funil. Tente novamente.");
    }
  };

  return (
    <Modal show={show} onClose={onClose} size="4xl">
      <Modal.Header>
        <div className="flex items-center gap-2">
          <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-gray-900 text-white text-xs font-bold">
            F
          </span>
          <div>
            <div className="text-lg font-semibold text-gray-800">
              Criar funil personalizado
            </div>
            <div className="text-xs text-gray-500">
              Defina o nome e construa as etapas do seu quadro.
            </div>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body>
        {/* visibilidade */}
        <div className="mb-4 flex items-center gap-2">
          <Badge color={visInfo.badge.color} className="capitalize">
            {visInfo.label}
          </Badge>
          {vis === "TEAM" && (
            <span className="text-xs text-gray-600">
              Time: <b>{defaultTeamName || "—"}</b>
            </span>
          )}
          {vis === "PRIVATE" && (
            <span className="text-xs text-gray-600">
              Dono: <b>{ownerUserId ? `#${ownerUserId}` : "—"}</b>
            </span>
          )}
        </div>

        {error && (
          <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        {/* Nome */}
        <div className="rounded-lg border border-gray-200 p-4 bg-white">
          <label className="text-sm font-medium text-gray-700 block mb-1">
            Nome do quadro *
          </label>
          <TextInput
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Ex: Pós-venda personalizado"
          />
        </div>

        {/* Etapas */}
        <div className="rounded-lg border border-gray-200 p-4 bg-white mt-4">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold text-gray-700">Etapas</h4>
            <Button size="xs" onClick={addStage}>
              Adicionar etapa
            </Button>
          </div>

          <div className="mt-3 space-y-2">
            {stages.map((s, i) => (
              <div
                key={i}
                className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 p-2"
              >
                <span className="text-xs text-gray-500 w-10 text-center">
                  {i + 1}º
                </span>
                <TextInput
                  className="flex-1"
                  value={s}
                  onChange={(e) => updateStage(i, e.target.value)}
                />
                <Button
                  size="xs"
                  color="light"
                  onClick={() => move(i, -1)}
                  disabled={i === 0}
                >
                  ↑
                </Button>
                <Button
                  size="xs"
                  color="light"
                  onClick={() => move(i, +1)}
                  disabled={i === stages.length - 1}
                >
                  ↓
                </Button>
                <Button
                  size="xs"
                  color="failure"
                  onClick={() => removeStage(i)}
                >
                  Remover
                </Button>
              </div>
            ))}
          </div>

          {/* Fechamento */}
          <div className="grid sm:grid-cols-2 gap-4 mt-4">
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">
                Etapa de fechamento (opcional)
              </label>
              <Select
                value={closingIndex}
                onChange={(e) => setClosingIndex(e.target.value)}
              >
                <option value="">— Não definido —</option>
                {stages.map((nm, i) => (
                  <option key={i} value={i}>
                    {nm || `Etapa ${i + 1}`}
                  </option>
                ))}
              </Select>
            </div>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer className="flex justify-end">
        <Button color="gray" onClick={onClose}>
          Cancelar
        </Button>
        <Button onClick={handleCreate} disabled={!canSave}>
          Criar funil
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
